import { Box, Button, Flex, Grid, GridItem, Text } from '@chakra-ui/react';
import { Breadcrumbs } from '@diamond/shared/ui';
import type { FormStepperStep } from '@diamond/sol-admin/components';
import { AdminLayout, FormStepper } from '@diamond/sol-admin/components';
import { ReactNode } from 'react';

interface VoucherFormLayoutProps {
  children: ReactNode;
  showSaveButton?: boolean;
  handleNext?: () => void;
  handleBack?: () => void;
  currentStep?: number;
  preview?: ReactNode;
  finalStepIndex?: number;
  isNextLoading?: boolean;
  onStepChange?: (stepIndex: number) => void;
  enableStepNavigation?: boolean;
  headerAction?: ReactNode;
  title?: string;
  breadcrumbLinks?: Array<{ title: string; link: string }>;
  nextButtonText?: string;
  backButtonText?: string;
  hideBackButton?: boolean;
}

const steps: FormStepperStep[] = [
  { title: 'Info Dasar' },
  { title: 'Target pengguna' },
  { title: 'Syarat Penggunaan' },
  { title: 'Ketentuan produk' },
];

const links = [
  { title: 'Beranda', link: '/' },
  { title: 'Voucher Management', link: '/vouchers' },
  { title: 'Tambah Voucher Baru', link: '/vouchers/new' },
];

export function VoucherFormLayout({
  children,
  showSaveButton = true,
  handleNext,
  handleBack,
  currentStep = 0,
  preview,
  finalStepIndex,
  isNextLoading = false,
  onStepChange,
  enableStepNavigation = false,
  headerAction,
  title = 'Tambah Voucher Baru',
  breadcrumbLinks = links,
  nextButtonText,
  backButtonText,
  hideBackButton = false,
}: VoucherFormLayoutProps) {
  const lastStepIndex =
    typeof finalStepIndex === 'number'
      ? Math.min(finalStepIndex, steps.length - 1)
      : steps.length - 1;
  const isLastStep = currentStep >= lastStepIndex;

  return (
    <AdminLayout>
      <Box pb="4">
        <Breadcrumbs links={breadcrumbLinks} useRouterDom />
      </Box>

      <Flex justify="space-between" align="center" mb="6">
        <Text fontSize="2xl" fontWeight="bold">
          {title}
        </Text>
        {headerAction}
      </Flex>

      {/* Stepper */}
      <Box
        px={{ base: 0, md: 2, lg: 4 }}
        pb={{ base: 5, md: 6 }}
        mb={{ base: 6, md: 8 }}
        borderBottomWidth="1px"
        borderColor="gray.200"
      >
        <FormStepper
          steps={steps}
          currentStep={currentStep}
          size="lg"
          containerProps={{
            maxW: 'container.md',
            mx: 'auto',
            w: 'full',
            sx: {
              '--stepper-indicator-size': '36px',
              '--stepper-icon-size': '18px',
              '--stepper-title-font-size': '14px',
              '--stepper-description-font-size': '12px',
            },
          }}
          onStepClick={onStepChange}
          allowClickToNavigate={enableStepNavigation}
        />
      </Box>

      {/* Main Content */}
      <Grid
        templateColumns={{
          base: '1fr',
          xl: preview ? '1fr 1fr' : '1fr',
        }}
        gap={8}
        alignItems="start"
      >
        <GridItem>
          <Box
            bg="white"
            p="6"
            borderRadius="lg"
            border="1px"
            borderColor="gray.200"
          >
            {children}
          </Box>

          {/* Action Buttons */}
          <Flex justify="flex-end" mt="6" gap={4}>
            {!hideBackButton && (
              <Button variant="outline" onClick={handleBack}>
                {backButtonText || 'Kembali'}
              </Button>
            )}

            <Button
              type="button"
              colorScheme="blue"
              onClick={handleNext}
              isLoading={isNextLoading}
              isDisabled={isNextLoading}
            >
              {nextButtonText || (isLastStep ? 'Buat Voucher' : 'Lanjut')}
            </Button>
          </Flex>
        </GridItem>

        {/* Preview Panel */}
        {preview && (
          <GridItem>
            <Box
              bg="#F2F2F7"
              p="6"
              borderRadius="lg"
              border="1px"
              borderColor="#E5E5EA"
              position="sticky"
              top="6"
            >
              {preview}
            </Box>
          </GridItem>
        )}
      </Grid>
    </AdminLayout>
  );
}
